import { Edit3, ImagePlus, PackageOpen, Plus, Search, SlidersHorizontal, Trash2, Upload, X, DollarSign } from 'lucide-react'
import { useEffect, useState, type FormEvent, type KeyboardEvent } from 'react'
import { useLiveTable } from '../hooks/useLiveTable'
import { supabase } from '../lib/supabase'
import { getCurrentEstablishmentId } from '../lib/tenant'

type Sector = 'kitchen' | 'bar' | 'reception' | 'grill' | 'pizzeria' | 'desserts' | 'pantry'
type Product = { id: string; category_id: string | null; name: string; description: string | null; barcode: string | null; price: number; promotional_price: number | null; is_daily_special:boolean; active: boolean; available: boolean; production_sector: Sector; preparation_minutes: number; image_url: string | null }
type Category = { id: string; name: string; slug: string; active: boolean; sort_order: number }
type OptionGroup = { id:string; product_id:string; name:string; selection_type:'single'|'multiple'; required:boolean; min_select:number; max_select:number; sort_order:number; active:boolean }
type OptionValue = { id:string; group_id:string; name:string; price_delta:number; sort_order:number; active:boolean }
type ProductForm = { category_id: string; name: string; description: string; barcode: string; price: string; promotional_price: string; is_daily_special:boolean; production_sector: Sector; preparation_minutes: string; active: boolean; available: boolean; control_stock:boolean; stock_quantity:string; minimum_stock:string; average_cost:string }
type InventoryItem = { id:string; linked_product_id:string|null; stock_quantity:number; minimum_stock:number; average_cost:number; active:boolean }

const emptyForm: ProductForm = { category_id:'', name:'', description:'', barcode:'', price:'', promotional_price:'', is_daily_special:false, production_sector:'kitchen', preparation_minutes:'15', active:true, available:true, control_stock:false, stock_quantity:'0', minimum_stock:'0', average_cost:'0' }
const sectors: Record<Sector,string> = { kitchen:'Cozinha', bar:'Drinks / Bar (vai para o KDS)', reception:'Recepção (cerveja/refrigerante)', grill:'Chapa/Grelha', pizzeria:'Pizzaria', desserts:'Sobremesas', pantry:'Copa' }
const money = new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'})

export function Products() {
  const { rows, loading, error, reload } = useLiveTable<Product>('products', 'name')
  const { rows: categories, reload: reloadCategories } = useLiveTable<Category>('categories', 'sort_order')
  const { rows: optionGroups, reload: reloadOptionGroups } = useLiveTable<OptionGroup>('product_option_groups','sort_order')
  const { rows: optionValues, reload: reloadOptionValues } = useLiveTable<OptionValue>('product_option_values','sort_order')
  const { rows: inventoryItems, reload: reloadInventoryItems } = useLiveTable<InventoryItem>('inventory_items','name')
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Product | null>(null)
  const [categoryOpen, setCategoryOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [categoryName, setCategoryName] = useState('')
  const [categoryError, setCategoryError] = useState('')
  const [form, setForm] = useState<ProductForm>(emptyForm)
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)
  const [barcodeScan,setBarcodeScan]=useState('')
  const [imageFile,setImageFile]=useState<File|null>(null)
  const [imagePreview,setImagePreview]=useState('')
  const [removeImage,setRemoveImage]=useState(false)
  const [variantProduct,setVariantProduct]=useState<Product|null>(null)
  const [variantError,setVariantError]=useState('')
  const [productSearch,setProductSearch]=useState('')
  const [bulkOpen,setBulkOpen]=useState(false)
  const [bulkSelected,setBulkSelected]=useState<string[]>([])
  const [bulkDraft,setBulkDraft]=useState<Record<string,string>>({})
  const [bulkSnapshot,setBulkSnapshot]=useState<Record<string,number>>({})
  const [bulkBusy,setBulkBusy]=useState(false)
  const [bulkError,setBulkError]=useState('')
  const [bulkNotice,setBulkNotice]=useState('')

  useEffect(() => {
    if (!open) {
      setEditing(null)
      setForm(emptyForm)
      setFormError('')
      setImageFile(null)
      setImagePreview('')
      setRemoveImage(false)
    }
  }, [open])

  function createProduct(barcode='') {
    setEditing(null)
    setForm({...emptyForm, barcode, category_id:categories[0]?.id || ''})
    setImageFile(null)
    setImagePreview('')
    setRemoveImage(false)
    setOpen(true)
  }

  function editProduct(product: Product) {
    setEditing(product)
    const stockItem=inventoryItems.find(item=>item.linked_product_id===product.id)
    setForm({
      category_id:product.category_id || '',
      name:product.name,
      description:product.description || '',
      barcode:product.barcode || '',
      price:String(product.price),
      promotional_price:product.promotional_price == null ? '' : String(product.promotional_price),
      is_daily_special:Boolean(product.is_daily_special),
      production_sector:product.production_sector,
      preparation_minutes:String(product.preparation_minutes),
      active:product.active,
      available:product.available,
      control_stock:Boolean(stockItem?.active),
      stock_quantity:String(stockItem?.stock_quantity ?? 0),
      minimum_stock:String(stockItem?.minimum_stock ?? 0),
      average_cost:String(stockItem?.average_cost ?? 0),
    })
    setImageFile(null)
    setImagePreview(product.image_url || '')
    setRemoveImage(false)
    setOpen(true)
  }

  function storagePathFromPublicUrl(url:string|null){
    if(!url)return null
    const marker='/storage/v1/object/public/product-images/'
    const index=url.indexOf(marker)
    return index>=0?decodeURIComponent(url.slice(index+marker.length)):null
  }

  async function uploadProductImage(file:File,productId:string,establishmentId:string){
    if(!['image/jpeg','image/png','image/webp'].includes(file.type))throw new Error('Use uma imagem JPG, PNG ou WebP.')
    if(file.size>5*1024*1024)throw new Error('A foto deve ter no máximo 5 MB.')
    const ext=file.type==='image/png'?'png':file.type==='image/webp'?'webp':'jpg'
    const path=`${establishmentId}/${productId}/${Date.now()}.${ext}`
    const {error:uploadError}=await supabase.storage.from('product-images').upload(path,file,{contentType:file.type,cacheControl:'3600',upsert:false})
    if(uploadError)throw uploadError
    return {path,url:supabase.storage.from('product-images').getPublicUrl(path).data.publicUrl}
  }

  async function deleteStoredImage(url:string|null){
    const path=storagePathFromPublicUrl(url)
    if(path)await supabase.storage.from('product-images').remove([path])
  }

  function chooseImage(file:File|null){
    if(!file)return
    if(!['image/jpeg','image/png','image/webp'].includes(file.type)){setFormError('Use uma imagem JPG, PNG ou WebP.');return}
    if(file.size>5*1024*1024){setFormError('A foto deve ter no máximo 5 MB.');return}
    if(imagePreview.startsWith('blob:'))URL.revokeObjectURL(imagePreview)
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
    setRemoveImage(false)
    setFormError('')
  }

  function handleBarcodeScan(event:KeyboardEvent<HTMLInputElement>){
    if(event.key!=='Enter')return
    event.preventDefault()
    const code=barcodeScan.trim()
    if(!code)return
    const product=rows.find(item=>(item.barcode||'')===code)
    if(product){editProduct(product);setBarcodeScan('');return}
    createProduct(code);setBarcodeScan('')
  }

  function openCategories() {
    setEditingCategory(null)
    setCategoryName('')
    setCategoryError('')
    setCategoryOpen(true)
  }

  function editCategory(category: Category) {
    setEditingCategory(category)
    setCategoryName(category.name)
    setCategoryError('')
  }

  function categorySlug(name:string) {
    return name.normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase().trim().replace(/[^a-z0-9]+/g,'-').replace(/(^-|-$)/g,'')
  }

  async function saveCategory(event: FormEvent) {
    event.preventDefault()
    const name=categoryName.trim()
    if (!name) { setCategoryError('Informe o nome da categoria.'); return }
    setSaving(true); setCategoryError('')
    const payload={name,slug:categorySlug(name),sort_order:editingCategory?.sort_order ?? categories.length,active:editingCategory?.active ?? true}
    const result=editingCategory
      ? await supabase.from('categories').update(payload).eq('id',editingCategory.id)
      : await supabase.from('categories').insert({...payload,establishment_id:await getCurrentEstablishmentId()})
    if (result.error) setCategoryError(result.error.message.includes('duplicate') ? 'Já existe uma categoria com esse nome.' : result.error.message)
    else { setEditingCategory(null); setCategoryName(''); await reloadCategories() }
    setSaving(false)
  }

  async function toggleCategory(category: Category) {
    const {error:updateError}=await supabase.from('categories').update({active:!category.active}).eq('id',category.id)
    if(updateError) setCategoryError(updateError.message); else await reloadCategories()
  }

  async function removeCategory(category: Category) {
    if(!window.confirm(`Excluir a categoria "${category.name}"?`)) return
    const {error:deleteError}=await supabase.from('categories').delete().eq('id',category.id)
    if(deleteError) setCategoryError(deleteError.message.includes('foreign key') ? 'Essa categoria possui produtos. Reorganize ou desative-a antes de excluir.' : deleteError.message)
    else { if(editingCategory?.id===category.id){setEditingCategory(null);setCategoryName('')} await reloadCategories() }
  }

  async function save(event: FormEvent) {
    event.preventDefault()
    setFormError('')
    setSaving(true)

    try {
      const price = Number(form.price.replace(',','.'))
      const promotionalPrice = form.promotional_price ? Number(form.promotional_price.replace(',','.')) : null
      const preparationMinutes = Number(form.preparation_minutes)

      if (!form.name.trim()) throw new Error('Informe o nome do produto.')
      if (!Number.isFinite(price) || price < 0) throw new Error('Informe um preço válido.')
      if (promotionalPrice !== null && (!Number.isFinite(promotionalPrice) || promotionalPrice < 0)) throw new Error('Informe um preço promocional válido.')
      if (!Number.isInteger(preparationMinutes) || preparationMinutes < 0) throw new Error('Informe um tempo de preparo válido.')
      const stockQuantity=Number(form.stock_quantity.replace(',','.'))
      const minimumStock=Number(form.minimum_stock.replace(',','.'))
      const averageCost=Number(form.average_cost.replace(',','.'))
      if(form.control_stock&&(![stockQuantity,minimumStock,averageCost].every(Number.isFinite)||stockQuantity<0||minimumStock<0||averageCost<0)) throw new Error('Confira estoque atual, estoque mínimo e custo unitário.')

      const payload = {
        category_id:form.category_id || null,
        name:form.name.trim(),
        description:form.description.trim() || null,
        barcode:form.barcode.trim() || null,
        price,
        promotional_price:promotionalPrice,
        is_daily_special:form.is_daily_special,
        production_sector:form.production_sector,
        preparation_minutes:form.production_sector==='reception' ? 0 : preparationMinutes,
        active:form.active,
        available:form.available,
        updated_at:new Date().toISOString(),
      }

      const establishmentId = await getCurrentEstablishmentId()

      if (editing) {
        let nextImageUrl=removeImage?null:editing.image_url
        let uploadedPath:string|null=null
        if(imageFile){
          const uploaded=await uploadProductImage(imageFile,editing.id,establishmentId)
          uploadedPath=uploaded.path
          nextImageUrl=uploaded.url
        }
        const { error:updateError } = await supabase.from('products').update({...payload,image_url:nextImageUrl}).eq('id',editing.id)
        if (updateError) {
          if(uploadedPath)await supabase.storage.from('product-images').remove([uploadedPath])
          throw updateError
        }
        if((removeImage||imageFile)&&editing.image_url&&editing.image_url!==nextImageUrl)await deleteStoredImage(editing.image_url)
        const {error:stockError}=await supabase.rpc('configure_product_direct_stock',{
          p_product_id:editing.id,
          p_enabled:form.control_stock,
          p_stock_quantity:Number(form.stock_quantity.replace(',','.'))||0,
          p_minimum_stock:Number(form.minimum_stock.replace(',','.'))||0,
          p_average_cost:Number(form.average_cost.replace(',','.'))||0,
        })
        if(stockError)throw stockError
        await reloadInventoryItems()
      } else {
        const {data:inserted,error:insertError}=await supabase.from('products').insert({...payload,establishment_id:establishmentId,image_url:null}).select('id').single()
        if (insertError) throw insertError
        if(imageFile&&inserted){
          try{
            const uploaded=await uploadProductImage(imageFile,inserted.id,establishmentId)
            const {error:imageUpdateError}=await supabase.from('products').update({image_url:uploaded.url}).eq('id',inserted.id)
            if(imageUpdateError){await supabase.storage.from('product-images').remove([uploaded.path]);throw imageUpdateError}
          }catch(imageError){
            await supabase.from('products').delete().eq('id',inserted.id)
            throw imageError
          }
        }
        if(inserted){
          const {error:stockError}=await supabase.rpc('configure_product_direct_stock',{
            p_product_id:inserted.id,
            p_enabled:form.control_stock,
            p_stock_quantity:Number(form.stock_quantity.replace(',','.'))||0,
            p_minimum_stock:Number(form.minimum_stock.replace(',','.'))||0,
            p_average_cost:Number(form.average_cost.replace(',','.'))||0,
          })
          if(stockError)throw stockError
          await reloadInventoryItems()
        }
      }

      await reload()
      setOpen(false)
    } catch (caught) {
      const message=caught instanceof Error ? caught.message : 'Não foi possível salvar o produto.'
      setFormError(/duplicate|unique/i.test(message)?'Esse código de barras já está cadastrado em outro produto.':message)
    } finally {
      setSaving(false)
    }
  }

  async function addOptionGroup(){
    if(!variantProduct)return
    const name=window.prompt('Nome da variação:', 'Escolha o sabor')?.trim()
    if(!name)return
    const type=(window.confirm('OK = escolher apenas UMA opção.\nCancelar = permitir VÁRIAS opções.')?'single':'multiple') as 'single'|'multiple'
    const establishmentId=await getCurrentEstablishmentId()
    const productGroups=optionGroups.filter(group=>group.product_id===variantProduct.id)
    const {error:e}=await supabase.from('product_option_groups').insert({establishment_id:establishmentId,product_id:variantProduct.id,name,selection_type:type,required:true,min_select:1,max_select:type==='single'?1:10,sort_order:productGroups.length,active:true})
    if(e)setVariantError(e.message);else await reloadOptionGroups()
  }

  async function addOptionValue(group:OptionGroup){
    if(!variantProduct)return
    const name=window.prompt('Nome da opção:', 'Frango')?.trim()
    if(!name)return
    const priceText=window.prompt('Acréscimo no preço (R$):\nUse 0 se não houver acréscimo.', '0,00')
    if(priceText===null)return
    const price=Number(priceText.replace(',','.'))
    if(!Number.isFinite(price)||price<0){setVariantError('Informe um acréscimo válido.');return}
    const establishmentId=await getCurrentEstablishmentId()
    const values=optionValues.filter(value=>value.group_id===group.id)
    const {error:e}=await supabase.from('product_option_values').insert({establishment_id:establishmentId,group_id:group.id,name,price_delta:price,sort_order:values.length,active:true})
    if(e)setVariantError(e.message);else await reloadOptionValues()
  }

  async function updateOptionGroup(group:OptionGroup,patch:Partial<OptionGroup>){
    const next:any={...patch}
    if(patch.selection_type==='single'){next.min_select=1;next.max_select=1}
    const {error:e}=await supabase.from('product_option_groups').update(next).eq('id',group.id)
    if(e)setVariantError(e.message);else await reloadOptionGroups()
  }

  async function removeOptionGroup(group:OptionGroup){
    if(!window.confirm('Excluir a variação "'+group.name+'" e todas as opções?'))return
    const {error:e}=await supabase.from('product_option_groups').delete().eq('id',group.id)
    if(e)setVariantError(e.message);else await Promise.all([reloadOptionGroups(),reloadOptionValues()])
  }

  async function editOptionValue(value:OptionValue){
    const name=window.prompt('Nome da opção:',value.name)?.trim()
    if(!name)return
    const priceText=window.prompt('Acréscimo no preço (R$):',String(value.price_delta).replace('.',','))
    if(priceText===null)return
    const price=Number(priceText.replace(',','.'))
    if(!Number.isFinite(price)||price<0){setVariantError('Informe um acréscimo válido.');return}
    const {error:e}=await supabase.from('product_option_values').update({name,price_delta:price}).eq('id',value.id)
    if(e)setVariantError(e.message);else await reloadOptionValues()
  }
  async function removeOptionValue(value:OptionValue){
    if(!window.confirm('Excluir a opção "'+value.name+'"?'))return
    const {error:e}=await supabase.from('product_option_values').delete().eq('id',value.id)
    if(e)setVariantError(e.message);else await reloadOptionValues()
  }
  async function toggleAvailability(product: Product) {
    const { error:updateError } = await supabase.from('products').update({available:!product.available,updated_at:new Date().toISOString()}).eq('id',product.id)
    if (updateError) window.alert(updateError.message)
    else await reload()
  }

  async function remove(product: Product) {
    if (!window.confirm(`Excluir "${product.name}" do cardápio?`)) return
    const { error:deleteError } = await supabase.from('products').delete().eq('id',product.id)
    if (deleteError) {
      window.alert(deleteError.message.includes('foreign key') ? 'Esse produto já está vinculado a pedidos. Desative-o em vez de excluir.' : deleteError.message)
      return
    }
    await reload()
  }

  const normalizeSearch=(value:string)=>value.normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLocaleLowerCase('pt-BR')
  const normalizedSearch=normalizeSearch(productSearch.trim())
  const filteredProducts=normalizedSearch
    ? rows.filter(product=>{
        const categoryName=categories.find(category=>category.id===product.category_id)?.name||''
        const haystack=[
          product.name,
          product.description||'',
          product.barcode||'',
          categoryName,
          sectors[product.production_sector],
        ].join(' ')
        return normalizeSearch(haystack).includes(normalizedSearch)
      })
    : rows

  function openBulkPrices(){
    setBulkSelected([])
    setBulkDraft({})
    setBulkSnapshot(Object.fromEntries(rows.map(item=>[item.id,Number(item.price)])))
    setBulkError('')
    setBulkNotice('')
    setBulkOpen(true)
  }
  function selectBulkProduct(product:Product,checked:boolean){
    setBulkError('')
    if(checked){
      if(bulkSelected.includes(product.id))return
      if(bulkSelected.length>=200){setBulkError('Limite de 200 produtos por alteração.');return}
      setBulkSelected(previous=>[...previous,product.id])
      setBulkDraft(previous=>({...previous,[product.id]:Number(product.price).toFixed(2).replace('.',',')}))
    }else setBulkSelected(previous=>previous.filter(id=>id!==product.id))
  }
  async function saveBulkPrices(){
    const changes=bulkSelected.map(id=>{
      const original=bulkSnapshot[id]
      const raw=(bulkDraft[id]||'').trim().replace(',','.')
      const value=Number(raw)
      return {id,old_price:original,new_price:value,raw}
    })
    if(!changes.length){setBulkError('Selecione pelo menos um produto.');return}
    if(changes.some(item=>!item.raw||!Number.isFinite(item.new_price)||item.new_price<0||item.new_price>999999||!/^\d+(?:[.,]\d{1,2})?$/.test(item.raw))){
      setBulkError('Informe preços válidos, com até duas casas decimais.');return
    }
    const modified=changes.filter(item=>item.old_price!==item.new_price)
    if(!modified.length){setBulkError('Nenhum preço foi alterado.');return}
    if(!window.confirm('CONFIRMAR ALTERAÇÃO DE PREÇOS\n\n'+modified.length+' produto(s) terão os preços de catálogo alterados.\nPedidos antigos não serão modificados.\n\nContinuar?'))return
    setBulkBusy(true);setBulkError('');setBulkNotice('')
    try{
      const {data,error:updateError}=await supabase.rpc('bulk_update_product_prices',{p_changes:modified.map(({id,old_price,new_price})=>({id,old_price,new_price}))})
      if(updateError)throw updateError
      await reload()
      setBulkNotice(String(data)+' preço(s) atualizados com sucesso.')
      setBulkOpen(false)
    }catch(err){setBulkError(err&&typeof err==='object'&&'message' in err?String(err.message):'Não foi possível atualizar os preços.')}
    finally{setBulkBusy(false)}
  }
  return <>
    <div className="page-heading">
      <div><span className="eyebrow dark">CARDÁPIO</span><h1>Produtos</h1><p>Itens, preços, disponibilidade e setor de produção.</p></div>
      <div className="header-actions"><button className="secondary-button" onClick={openBulkPrices}><DollarSign size={17}/> Editar preços em massa</button><button className="ghost-button" onClick={openCategories}>Categorias</button><button className="primary-button small" onClick={()=>createProduct()}><Plus size={18}/> Novo produto</button></div>
    </div>
    {error && <div className="form-error">{error}</div>}
    {bulkNotice&&<div className="notice">{bulkNotice}</div>}
    <article className="panel" style={{marginBottom:16}}><div className="panel-heading"><div><span className="eyebrow dark">LEITOR</span><h3>Código de barras</h3></div></div><label style={{display:'grid',gap:6,marginTop:12}}>Escaneie ou digite o código<input value={barcodeScan} onChange={e=>setBarcodeScan(e.target.value)} onKeyDown={handleBarcodeScan} placeholder="EAN-13, EAN-8, Code 128 ou QR Code"/><small>Se o código já existir, abre o produto. Se for novo, inicia um cadastro já com o código preenchido.</small></label></article>
    <article className="panel products-search-panel">
      <div className="products-search-wrap">
        <Search size={19}/>
        <input
          value={productSearch}
          onChange={e=>setProductSearch(e.target.value)}
          placeholder="Buscar por nome, categoria, código de barras ou descrição..."
          aria-label="Buscar produtos"
        />
        {productSearch&&<button type="button" className="products-search-clear" onClick={()=>setProductSearch('')} aria-label="Limpar busca"><X size={16}/></button>}
      </div>
      <small className="products-search-count">{filteredProducts.length} de {rows.length} produto(s)</small>
    </article>
    <article className="panel table-panel">{loading ? <div className="loading">Carregando produtos…</div> : rows.length ? filteredProducts.length ? <div className="data-table">
      {filteredProducts.map(product => <div className="data-row product-row" key={product.id}>
        <div className="product-thumb">{product.image_url?<img src={product.image_url} alt=""/>:<PackageOpen size={18}/>}</div>
        <div><strong>{product.name}</strong><small>{product.barcode?`Código ${product.barcode} • `:''}{sectors[product.production_sector]} • {product.preparation_minutes} min</small>{inventoryItems.find(item=>item.linked_product_id===product.id)?.active&&<small className="product-stock-line">Estoque: <b>{Number(inventoryItems.find(item=>item.linked_product_id===product.id)?.stock_quantity||0).toLocaleString('pt-BR')}</b> un · mínimo {Number(inventoryItems.find(item=>item.linked_product_id===product.id)?.minimum_stock||0).toLocaleString('pt-BR')}</small>}</div>
        <button className={product.available && product.active ? 'badge success availability-button' : 'badge availability-button'} onClick={() => void toggleAvailability(product)}>{!product.active ? 'Inativo' : product.available ? 'Disponível' : 'Indisponível'}</button>
        <b>{money.format(product.promotional_price ?? product.price)}</b>
        <div className="row-actions"><button aria-label="Variações do produto" title="Variações" onClick={()=>{setVariantProduct(product);setVariantError('')}}><SlidersHorizontal size={16}/></button><button aria-label="Editar produto" onClick={() => editProduct(product)}><Edit3 size={16}/></button><button className="danger-action" aria-label="Excluir produto" onClick={() => void remove(product)}><Trash2 size={16}/></button></div>
      </div>)}
    </div> : <div className="empty-state compact"><Search size={34}/><strong>Nenhum produto encontrado</strong><p>Tente outro nome, categoria ou código.</p></div> : <div className="empty-state"><PackageOpen size={36}/><strong>Cardápio ainda vazio</strong><p>Cadastre o primeiro produto do Gonçalo.</p></div>}</article>

    {bulkOpen&&<div className="modal-backdrop" role="presentation" onMouseDown={()=>!bulkBusy&&setBulkOpen(false)}>
      <section className="modal-card modal-wide" role="dialog" aria-modal="true" aria-label="Editar preços em massa" onMouseDown={event=>event.stopPropagation()}>
        <header><div><span className="eyebrow dark">CARDÁPIO</span><h2>Editar preços em massa</h2></div><button type="button" className="modal-close" aria-label="Fechar" disabled={bulkBusy} onClick={()=>setBulkOpen(false)}><X/></button></header>
        <p>Escolha os produtos, altere os preços de catálogo e confirme. Os valores de pedidos existentes não são alterados.</p>
        <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',marginBottom:14}}>
          <span className="badge">{bulkSelected.length} selecionado(s)</span>
          <button type="button" className="ghost-button" disabled={bulkBusy} onClick={()=>{
            const choices=filteredProducts.slice(0,200)
            setBulkSelected(choices.map(item=>item.id))
            setBulkDraft(previous=>({...previous,...Object.fromEntries(choices.map(item=>[item.id,previous[item.id]??Number(item.price).toFixed(2).replace('.',',')]))}))
            setBulkError(filteredProducts.length>200?'Primeiros 200 selecionados. Refine a busca para outros itens.':'')
          }}>Selecionar exibidos (até 200)</button>
          <button type="button" className="ghost-button" disabled={bulkBusy} onClick={()=>{setBulkSelected([]);setBulkError('')}}>Limpar seleção</button>
        </div>
        <div className="bulk-price-list">
          {filteredProducts.map(product=><div className="bulk-price-row" key={product.id}>
            <label className="bulk-price-check"><input type="checkbox" checked={bulkSelected.includes(product.id)} disabled={bulkBusy} onChange={event=>selectBulkProduct(product,event.target.checked)}/><span>{product.name}</span></label>
            <small>Atual: {money.format(Number(bulkSnapshot[product.id]??product.price))}</small>
            <label className="bulk-price-value">Novo preço (R$)<input inputMode="decimal" aria-label={'Novo preço para '+product.name} disabled={!bulkSelected.includes(product.id)||bulkBusy} value={bulkDraft[product.id]??Number(product.price).toFixed(2).replace('.',',')} onChange={event=>setBulkDraft(previous=>({...previous,[product.id]:event.target.value}))}/></label>
          </div>)}
          {!filteredProducts.length&&<p>Nenhum produto encontrado. Altere a busca na página Produtos.</p>}
        </div>
        {bulkError&&<div className="form-error" role="alert">{bulkError}</div>}
        <div className="form-actions"><button type="button" className="ghost-button" disabled={bulkBusy} onClick={()=>setBulkOpen(false)}>Cancelar</button><button type="button" className="primary-button" disabled={bulkBusy||bulkSelected.length===0} onClick={()=>void saveBulkPrices()}>{bulkBusy?'Salvando…':'Conferir e salvar preços'}</button></div>
      </section>
    </div>}
    {variantProduct&&<div className="modal-backdrop" role="presentation" onMouseDown={()=>setVariantProduct(null)}><section className="modal-card modal-wide" role="dialog" aria-modal="true" onMouseDown={event=>event.stopPropagation()}><header><div><span className="eyebrow dark">VARIAÇÕES</span><h2>{variantProduct.name}</h2></div><button className="modal-close" onClick={()=>setVariantProduct(null)}><X/></button></header>
<div className="variant-admin-head"><p>Crie escolhas como sabor, tamanho ou adicionais. Cada opção pode acrescentar valor ao produto.</p><button className="primary-button small" type="button" onClick={()=>void addOptionGroup()}><Plus size={16}/> Nova variação</button></div>
{variantError&&<div className="form-error">{variantError}</div>}
<div className="variant-admin-list">{optionGroups.filter(group=>group.product_id===variantProduct.id).map(group=><article className="variant-admin-group" key={group.id}><div className="variant-admin-group-head"><div><strong>{group.name}</strong><small>{group.selection_type==='single'?'Escolha única':'Múltipla escolha'} · {group.required?'Obrigatória':'Opcional'}</small></div><div className="row-actions"><button type="button" title="Alternar obrigatória/opcional" onClick={()=>void updateOptionGroup(group,{required:!group.required,min_select:!group.required?1:0})}>{group.required?'Obrig.':'Opc.'}</button><button type="button" title="Alternar tipo" onClick={()=>void updateOptionGroup(group,{selection_type:group.selection_type==='single'?'multiple':'single',max_select:group.selection_type==='single'?10:1})}>{group.selection_type==='single'?'1':'✓✓'}</button><button type="button" className="danger-action" onClick={()=>void removeOptionGroup(group)}><Trash2 size={15}/></button></div></div><div className="variant-values">{optionValues.filter(value=>value.group_id===group.id).map(value=><div className="variant-value" key={value.id}><span>{value.name}</span><b>{Number(value.price_delta)>0?'+ '+money.format(Number(value.price_delta)):'Sem acréscimo'}</b><div className="row-actions"><button type="button" onClick={()=>void editOptionValue(value)}><Edit3 size={14}/></button><button type="button" className="danger-action" onClick={()=>void removeOptionValue(value)}><Trash2 size={14}/></button></div></div>)}<button type="button" className="ghost-button add-variant-option" onClick={()=>void addOptionValue(group)}><Plus size={15}/> Adicionar opção</button></div></article>)}</div>
{!optionGroups.some(group=>group.product_id===variantProduct.id)&&<div className="empty-state compact"><SlidersHorizontal size={34}/><strong>Sem variações</strong><p>Exemplo: “Escolha o sabor” → Frango / Bacon e calabresa.</p></div>}
</section></div>}
    {open && <div className="modal-backdrop" role="presentation" onMouseDown={() => !saving && setOpen(false)}>
      <section className="modal-card modal-wide" role="dialog" aria-modal="true" aria-labelledby="product-form-title" onMouseDown={event => event.stopPropagation()}>
        <header><div><span className="eyebrow dark">CARDÁPIO</span><h2 id="product-form-title">{editing ? 'Editar produto' : 'Novo produto'}</h2></div><button className="modal-close" onClick={() => setOpen(false)} aria-label="Fechar"><X/></button></header>
        <form className="entity-form" onSubmit={save}>
          <div className="product-image-editor">
            <div className="product-image-preview">{imagePreview&&!removeImage?<img src={imagePreview} alt="Prévia da foto do produto"/>:<ImagePlus size={34}/>}</div>
            <div className="product-image-controls">
              <strong>Foto do produto</strong>
              <small>JPG, PNG ou WebP · até 5 MB. A foto aparece no cardápio do QR Code.</small>
              <label className="ghost-button product-upload-button"><Upload size={17}/> {imagePreview&&!removeImage?'Trocar foto':'Adicionar foto'}<input type="file" accept="image/jpeg,image/png,image/webp" onChange={e=>chooseImage(e.target.files?.[0]||null)}/></label>
              {imagePreview&&!removeImage&&<button type="button" className="text-button danger-text" onClick={()=>{setImageFile(null);setImagePreview('');setRemoveImage(true)}}><Trash2 size={16}/> Remover foto</button>}
            </div>
          </div>
          <label>Nome do produto<input value={form.name} onChange={e => setForm({...form,name:e.target.value})} required autoFocus/></label>
          <label>Descrição<textarea rows={3} value={form.description} onChange={e => setForm({...form,description:e.target.value})} placeholder="Ingredientes e apresentação"/></label>
          <label>Código de barras / EAN / GTIN<input value={form.barcode} onChange={e=>setForm({...form,barcode:e.target.value.trimStart()})} placeholder="Passe o leitor aqui ou digite o código"/><small>Compatível com EAN-13, EAN-8, Code 128 e conteúdo lido por QR Code/2D.</small></label>
          <div className="form-grid">
            <label>Categoria<select value={form.category_id} onChange={e => setForm({...form,category_id:e.target.value})}><option value="">Sem categoria</option>{categories.filter(category => category.active).map(category => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label>
            <label>Setor<select value={form.production_sector} onChange={e => setForm({...form,production_sector:e.target.value as Sector,preparation_minutes:e.target.value==='reception'?'0':form.preparation_minutes})}>{Object.entries(sectors).map(([value,label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          </div>
          <div className="form-grid three">
            <label>Preço (R$)<input inputMode="decimal" value={form.price} onChange={e => setForm({...form,price:e.target.value})} placeholder="0,00" required/></label>
            <label>Promocional (R$)<input inputMode="decimal" value={form.promotional_price} onChange={e => setForm({...form,promotional_price:e.target.value})} placeholder="Opcional"/></label>
            <label>Preparo (min)<input type="number" min="0" value={form.preparation_minutes} onChange={e => setForm({...form,preparation_minutes:e.target.value})} required disabled={form.production_sector==='reception'}/></label>
          </div>
          <label className="toggle-line" style={{padding:12,border:"1px solid #dadada",borderRadius:10,marginBottom:12}}><input type="checkbox" checked={form.is_daily_special} onChange={e=>setForm({...form,is_daily_special:e.target.checked})}/><span><strong>🍽️ Prato do dia no cardápio público</strong><small>Destacar este produto quando estiver ativo e disponível. Desmarque ao terminar.</small></span></label>
          <div className="product-stock-box">
            <label className="toggle-line"><input type="checkbox" checked={form.control_stock} onChange={e=>setForm({...form,control_stock:e.target.checked})}/><span><strong>Controlar estoque deste produto</strong><small>Ideal para cervejas, refrigerantes e itens vendidos por unidade.</small></span></label>
            {form.control_stock&&<div className="form-grid three">
              <label>Estoque atual<input inputMode="decimal" value={form.stock_quantity} onChange={e=>setForm({...form,stock_quantity:e.target.value})} placeholder="0"/></label>
              <label>Estoque mínimo<input inputMode="decimal" value={form.minimum_stock} onChange={e=>setForm({...form,minimum_stock:e.target.value})} placeholder="0"/></label>
              <label>Custo unitário (R$)<input inputMode="decimal" value={form.average_cost} onChange={e=>setForm({...form,average_cost:e.target.value})} placeholder="0,00"/></label>
            </div>}
          </div>
          <div className="toggle-grid"><label><input type="checkbox" checked={form.active} onChange={e => setForm({...form,active:e.target.checked})}/> Produto ativo</label><label><input type="checkbox" checked={form.available} onChange={e => setForm({...form,available:e.target.checked})}/> Disponível para venda</label></div>
          {formError && <div className="form-error" role="alert">{formError}</div>}
          <div className="form-actions"><button type="button" className="ghost-button" onClick={() => setOpen(false)}>Cancelar</button><button className="primary-button" disabled={saving}>{saving ? 'Salvando…' : 'Salvar produto'}</button></div>
        </form>
      </section>
    </div>}
    {categoryOpen && <div className="modal-backdrop" role="presentation" onMouseDown={() => !saving && setCategoryOpen(false)}>
      <section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="category-form-title" onMouseDown={event => event.stopPropagation()}>
        <header><div><span className="eyebrow dark">CARDÁPIO</span><h2 id="category-form-title">Categorias</h2></div><button className="modal-close" onClick={() => setCategoryOpen(false)} aria-label="Fechar"><X/></button></header>
        <form className="entity-form" onSubmit={saveCategory}><label>{editingCategory ? 'Editar categoria' : 'Nova categoria'}<input value={categoryName} onChange={event => setCategoryName(event.target.value)} placeholder="Ex.: Porções" autoFocus/></label>{categoryError && <div className="form-error">{categoryError}</div>}<div className="form-actions"><button type="button" className="ghost-button" onClick={() => {setEditingCategory(null);setCategoryName('');setCategoryError('')}}>Limpar</button><button className="primary-button" disabled={saving}>{saving ? 'Salvando…' : editingCategory ? 'Salvar categoria' : 'Adicionar categoria'}</button></div></form>
        <div className="category-list">{categories.map(category => <div className="category-row" key={category.id}><div><strong>{category.name}</strong><small>{category.active ? 'Disponível no cardápio' : 'Desativada'}</small></div><button className={category.active ? 'badge success availability-button' : 'badge availability-button'} onClick={() => void toggleCategory(category)}>{category.active ? 'Ativa' : 'Inativa'}</button><div className="row-actions"><button aria-label="Editar categoria" onClick={() => editCategory(category)}><Edit3 size={16}/></button><button className="danger-action" aria-label="Excluir categoria" onClick={() => void removeCategory(category)}><Trash2 size={16}/></button></div></div>)}</div>
      </section>
    </div>}
  </>
}